import { useState, useMemo, useCallback, useEffect } from 'react';
import { 
  CustomFieldDefinition, 
  UserCustomFieldValue, 
  CustomFieldFormValues,
  UpdateUserCustomFieldsRequest 
} from '../../types/customFields';
import { convertUserFieldsToFormValues, convertFormValuesToApiRequest } from '../../utils/customFieldsUtils';

export interface UseCustomFieldsFormReturn {
  // Field selection state
  selectedFieldIds: number[];
  availableFields: CustomFieldDefinition[];
  selectedDefinitions: CustomFieldDefinition[];
  
  // Formik integration
  formInitialValues: CustomFieldFormValues;
  
  // Field management actions
  addField: (id: number) => void;
  removeField: (id: number) => void;
  
  // Form submission preparation
  prepareSubmissionData: (values: CustomFieldFormValues) => {
    apiRequest: UpdateUserCustomFieldsRequest;
    fieldsToDelete: CustomFieldDefinition[];
  };
  
  // Utility
  resetFieldSelection: () => void;
}

/**
 * Hook for managing custom fields form state and logic
 * Handles field selection, form values, and submission preparation
 */
export const useCustomFieldsForm = (
  definitions: CustomFieldDefinition[],
  userFields: UserCustomFieldValue[]
): UseCustomFieldsFormReturn => {
  
  // Initialize selected fields based on existing user field values
  const initialSelectedFieldIds = useMemo(() => {
    const existingIds = userFields
      .map((uf) => definitions.find((d) => d.name === uf.fieldName)?.id)
      .filter((id): id is number => typeof id === 'number');
    return Array.from(new Set(existingIds));
  }, [userFields, definitions]);

  // Field selection state
  const [selectedFieldIds, setSelectedFieldIds] = useState<number[]>(initialSelectedFieldIds);

  // Update selected fields when data loads (fix for initialization timing issue)
  useEffect(() => {
    if (initialSelectedFieldIds.length > 0 && selectedFieldIds.length === 0) {
      setSelectedFieldIds(initialSelectedFieldIds);
    }
  }, [initialSelectedFieldIds, selectedFieldIds.length]);

  // Computed available and selected fields
  const availableFields = useMemo(() => {
    return definitions.filter((d) => !selectedFieldIds.includes(d.id));
  }, [definitions, selectedFieldIds]);

  const selectedDefinitions = useMemo(() => {
    return definitions.filter((d) => selectedFieldIds.includes(d.id));
  }, [definitions, selectedFieldIds]);

  // Generate Formik initial values
  const formInitialValues = useMemo(() => {
    return convertUserFieldsToFormValues(userFields, definitions);
  }, [userFields, definitions]);

  // Field management actions
  const addField = useCallback((id: number) => {
    setSelectedFieldIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const removeField = useCallback((id: number) => {
    setSelectedFieldIds((prev) => prev.filter((fid) => fid !== id));
  }, []);

  const resetFieldSelection = useCallback(() => {
    setSelectedFieldIds(initialSelectedFieldIds);
  }, [initialSelectedFieldIds]);

  // Prepare submission data
  const prepareSubmissionData = useCallback((values: CustomFieldFormValues) => {
    // Find fields that were removed from selection but had existing values
    const fieldsToDelete = definitions.filter(
      (def) => 
        !selectedDefinitions.find((sd) => sd.id === def.id) && 
        userFields.find((uf) => uf.fieldName === def.name)
    );

    // Filter values to only include selected fields
    const filteredValues: CustomFieldFormValues = {};
    selectedDefinitions.forEach((definition) => {
      const key = definition.id.toString();
      if (values.hasOwnProperty(key)) {
        filteredValues[key] = values[key];
      }
    });

    // Convert to API request format
    const apiRequest = convertFormValuesToApiRequest(filteredValues, definitions);

    return {
      apiRequest,
      fieldsToDelete,
    };
  }, [definitions, selectedDefinitions, userFields]);

  return {
    selectedFieldIds,
    availableFields,
    selectedDefinitions,
    formInitialValues,
    addField,
    removeField,
    prepareSubmissionData,
    resetFieldSelection,
  };
};

export default useCustomFieldsForm;